import demoStudentJson from '@/data/demoStudent.json';

export type UserApiPayload = {
  user: Record<string, unknown>;
  students?: {
    studentsList?: Record<string, unknown>[];
    schoolName?: string;
    schoolShortName?: string;
    schoolTypeConfigured?: string;
  };
  schools?: unknown;
};

function mergeOktaClaims(authState: unknown): Record<string, unknown> {
  const state = authState as {
    accessToken?: { claims?: Record<string, unknown> };
    idToken?: { claims?: Record<string, unknown> };
  } | null;

  return {
    ...(state?.accessToken?.claims ?? {}),
    ...(state?.idToken?.claims ?? {}),
  };
}

/** Best-effort role from Okta JWT claims when `/user` is unavailable. */
export function roleFromOktaClaims(authState: unknown): string {
  const claims = mergeOktaClaims(authState);
  const direct = [
    claims.role,
    claims.userRole,
    claims.primaryRole,
    claims.k12_role,
    claims.k12Role,
  ];

  for (const value of direct) {
    const role = String(value ?? '').trim().toUpperCase();
    if (role) return role;
  }

  const groups = claims.groups;
  if (Array.isArray(groups)) {
    for (const group of groups) {
      const g = String(group ?? '').trim().toUpperCase();
      if (g === 'TEACHER' || g.includes('TEACHER')) return 'TEACHER';
    }
  }

  return '';
}

export function isTeacherUserApiFallbackEnabled(): boolean {
  return String(import.meta.env.VITE_TEACHER_USER_API_FALLBACK ?? '').toLowerCase() === 'true';
}

/** Use demo teacher payload when `/user` fails for teacher accounts (or dev flag is on). */
export function shouldUseTeacherUserApiFallback(authState: unknown, apiFailed: boolean): boolean {
  if (!apiFailed) return false;
  if (isTeacherUserApiFallbackEnabled()) return true;
  return roleFromOktaClaims(authState) === 'TEACHER';
}

/** API-shaped payload from `demoStudent.json`, merged with the signed-in Okta identity. */
export function buildTeacherUserApiFallback(authState: unknown): UserApiPayload {
  const claims = mergeOktaClaims(authState);
  const demo = (
    typeof structuredClone === 'function'
      ? structuredClone(demoStudentJson)
      : JSON.parse(JSON.stringify(demoStudentJson))
  ) as UserApiPayload;

  const user: Record<string, unknown> = { ...(demo.user ?? {}) };
  user.role = 'TEACHER';

  const identityId = claims.identityId ?? claims.sub;
  if (identityId != null && String(identityId).trim()) {
    user.identityId = String(identityId).trim();
  }
  if (claims.given_name != null && String(claims.given_name).trim()) {
    user.fname = String(claims.given_name).trim();
  } else if (claims.fname != null && String(claims.fname).trim()) {
    user.fname = String(claims.fname).trim();
  }
  if (claims.family_name != null && String(claims.family_name).trim()) {
    user.lname = String(claims.family_name).trim();
  } else if (claims.lname != null && String(claims.lname).trim()) {
    user.lname = String(claims.lname).trim();
  }
  if (claims.schoolId != null && String(claims.schoolId).trim()) {
    user.schoolId = String(claims.schoolId).trim();
  }
  if (claims.roleId != null && claims.roleId !== '') {
    user.roleId = claims.roleId;
  }

  return { ...demo, user };
}
